import { z } from 'zod';
import { allHtmlParsers } from '@utils/ast';
import { bindInputWithOptionsMaker } from '../util.make-input-with-options.js';

const makeInputWithOptions = bindInputWithOptionsMaker();
const fractionInput = makeInputWithOptions({
  entry: {
    schema: z.object({
      type: z.literal("fraction").default("fraction"),
      number: z.string().default(""),
      numerator: z.string().default(""),
      denominator: z.string().default("")
    }),
    shouldValidate: node => node.correctAnswers.length > 0,
    validate: (input, entry) => {
      return input.correctAnswers.some(correctAnswer => (input.styling.hideNumber || correctAnswer.number.trim() === entry.number.trim()) && correctAnswer.numerator.trim() === entry.numerator.trim() && correctAnswer.denominator.trim() === entry.denominator.trim());
    },
    parseLegacyDetailItem: (item, context) => {
      if (context.relatedTask.taskType === "WriteAnswers" && context.relatedTask.answerType === "InText" && item && typeof item === "object" && "numerator" in item && "denominator" in item && "number" in item) {
        return [context.relatedInput?.id ?? `${context.relatedTask.id}-${context.indexInDetails}`, {
          type: "fraction",
          number: item.number,
          denominator: item.denominator,
          numerator: item.numerator
        }];
      }
    }
  },
  getCorrectOption: (node, entry) => {
    const hasNumberInput = !node.styling.hideNumber;
    const answerDiffs = node.correctAnswers.map((correctAnswer, index) => {
      // Parse out input and correct answer as number values
      const inputNumber = hasNumberInput ? parseInt(entry?.number || "0", 10) : 0;
      const inputFraction = parseInt(entry?.numerator || "0", 10) / parseInt(entry?.denominator || "1", 10);
      const correctNumber = hasNumberInput ? parseInt(correctAnswer.number || "0", 10) : 0;
      const correctFraction = parseInt(correctAnswer.numerator || "0", 10) / parseInt(correctAnswer.denominator || "1", 10);
      let diff = 1 + Math.abs(inputNumber + inputFraction - (correctNumber + correctFraction));

      // We consider exact matches to be especially important, so
      // decrease the diff each time the user matches an input
      if (hasNumberInput && entry?.number === correctAnswer.number) {
        diff = diff / 1.1;
      }
      if (entry?.numerator === correctAnswer.numerator) {
        diff = diff / 1.1;
      }
      if (entry?.denominator === correctAnswer.denominator) {
        diff = diff / 1.1;
      }
      return {
        diff,
        index
      };
    });
    const correctAnswerIndex = answerDiffs.sort((a, b) => a.diff - b.diff)[0]?.index;
    return node.correctAnswers[correctAnswerIndex ?? 0];
  },
  parsers: {
    fractioninput: (node, context) => {
      const inputContext = context.extra;
      const index = Array.from(node.parentElement?.children ?? []).filter(child => !Object.keys(allHtmlParsers).includes(child.tagName.toLowerCase())).indexOf(node);
      const correctAnswers = (() => {
        const answers = Array.from(node.querySelectorAll("correctanswer")).map(answerNode => {
          const number = answerNode.querySelector("correctnumber")?.textContent?.trim() || "0";
          const numerator = answerNode.querySelector("correctnumerator")?.textContent?.trim() || "0";
          const denominator = answerNode.querySelector("correctdenominator")?.textContent?.trim() || "0";
          return {
            number,
            numerator,
            denominator
          };
        });
        const children = Array.from(node.children);
        const baseCorrectNumber = children.find(it => it.tagName.toLowerCase() === "correctnumber")?.textContent?.trim();
        const baseCorrectNumerator = children.find(it => it.tagName.toLowerCase() === "correctnumerator")?.textContent?.trim();
        const baseCorrectDenominator = children.find(it => it.tagName.toLowerCase() === "correctdenominator")?.textContent?.trim();
        if (baseCorrectNumerator && baseCorrectDenominator) {
          answers.unshift({
            number: baseCorrectNumber || "0",
            numerator: baseCorrectNumerator,
            denominator: baseCorrectDenominator
          });
        }
        return answers;
      })();
      return {
        id: `${inputContext?.taskId ? `${inputContext.taskId}-` : ""}${index}`,
        nodeType: "fraction-input",
        correctAnswers,
        styling: {
          hideNumber: node.getAttribute("hideNumber") === "true"
        }
      };
    }
  }
});

export { fractionInput };
//# sourceMappingURL=input.fraction.js.map
